import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Smartico } from '../../core/Smartico'
import { TInboxMessage } from '@smartico/public-api/dist/SmarticoLib'

interface InboxState {
	messages: TInboxMessage[]
	loading: boolean
	allMessagesLoaded: boolean
	error: string | null
	messageRange: { from: number; to: number }
}

const initialState: InboxState = {
	messages: [],
	loading: false,
	allMessagesLoaded: false,
	error: null,
	messageRange: { from: 0, to: 19 },
}

export const fetchInboxMessages = createAsyncThunk('inbox/fetchInboxMessages', async ({ from, to }: { from: number; to: number }, { rejectWithValue }) => {
	try {
		const smarticoApi = await Smartico.apiReady
		const response = await smarticoApi.getInboxMessages({ from, to })
		return response
	} catch (error) {
		return rejectWithValue('Failed to fetch messages')
	}
})

export const loadMoreMessages = createAsyncThunk('inbox/loadMoreMessages', async (_, { getState, rejectWithValue }) => {
	const state = getState() as { inbox: InboxState }
	const {
		messageRange: { from, to },
		allMessagesLoaded,
	} = state.inbox

	if (allMessagesLoaded) {
		return rejectWithValue('All messages are already loaded')
	}

	try {
		const smarticoApi = await Smartico.apiReady
		const response = await smarticoApi.getInboxMessages({ from, to })

		const newFrom = to + 1
		const newTo = to + 20
		const isAllMessagesLoaded = response.length < to - from + 1

		return { messages: response, newFrom, newTo, allMessagesLoaded: isAllMessagesLoaded }
	} catch (error) {
		return rejectWithValue('Failed to load more messages')
	}
})

export const markAllMessagesAsRead = createAsyncThunk('inbox/markAllMessagesAsRead', async (_, { rejectWithValue }) => {
	try {
		const smarticoApi = await Smartico.apiReady
		await smarticoApi.markAllInboxMessagesAsRead()
		return
	} catch (error) {
		return rejectWithValue('Failed to mark all messages as read')
	}
})


export const subscribeToMessagesUpdates = () => async (dispatch: any) => {
	try {
		const smarticoApi = await Smartico.apiReady
		await smarticoApi.getInboxMessages({
			onUpdate: (updatedMessages: TInboxMessage[]) => {
				if (updatedMessages && updatedMessages.length > 0) {
					dispatch(updateInbox(updatedMessages))
				}
			},
		})
	} catch (error) {
		console.error('Error subscribing to messages updates:', error)
	}
}


const inboxSlice = createSlice({
	name: 'inbox',
	initialState,
	reducers: {

		updateInbox(state, action) {
            state.messages = action.payload
        },

		toggleFavorite(state, action: PayloadAction<{ messageGuid: string; isFavorite: boolean }>) {
			const { messageGuid, isFavorite } = action.payload
			const message = state.messages.find((msg) => msg.message_guid === messageGuid)
			if (message) {
				message.favorite = isFavorite
			}
		},
		deleteMessage(state, action: PayloadAction<string>) {
			state.messages = state.messages.filter((msg) => msg.message_guid !== action.payload)
		},
		markMessageAsRead(state, action: PayloadAction<string>) {
			const message = state.messages.find((msg) => msg.message_guid === action.payload)
			if (message) {
				message.read = true
			}

			
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchInboxMessages.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(fetchInboxMessages.fulfilled, (state, action) => {
				state.loading = false
				state.messages = [...state.messages, ...action.payload]
				if (action.payload.length < state.messageRange.to - state.messageRange.from + 1) {
					state.allMessagesLoaded = true
				}
			})
			.addCase(fetchInboxMessages.rejected, (state, action) => {
				state.loading = false
				state.error = action.payload as string
			})
			.addCase(loadMoreMessages.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(loadMoreMessages.fulfilled, (state, action) => {
				const { messages, newFrom, newTo, allMessagesLoaded } = action.payload
				state.loading = false
				state.messages = [...state.messages, ...messages]
				state.messageRange = { from: newFrom, to: newTo }
				state.allMessagesLoaded = allMessagesLoaded
			})
			.addCase(loadMoreMessages.rejected, (state, action) => {
				state.loading = false
				state.error = action.payload as string
			})
			.addCase(markAllMessagesAsRead.fulfilled, (state) => {
				state.messages.forEach((msg) => (msg.read = true))
			})
			.addCase(markAllMessagesAsRead.rejected, (state, action) => {
				state.error = action.payload as string
			})
	},
})

export const { toggleFavorite, deleteMessage, markMessageAsRead,updateInbox } = inboxSlice.actions

export default inboxSlice.reducer
